import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback, useState } from "react";
import { samplesApi } from "@/services/api";
import type {
  EvidenceDownloadReceipt,
  EvidenceFileKind,
  EvidenceFilesResponse,
  EvidenceIntegrity,
  StoredHashScope,
} from "@/types";
import {
  type EvidenceFailure,
  parseEvidenceFailure,
} from "@/utils/evidenceError";

const EVIDENCE_FILES_KEY = (sampleId: number) =>
  ["samples", "evidence-files", sampleId] as const;

/**
 * 증거 원본의 **상태만** 조회한다 (감사 행을 남기지 않는 경로).
 *
 * 재시도 정책: 401 만 재시도한다. 앱 전체가 "401 → 인터셉터가 토큰 갱신 후
 * reject → React Query 재시도" 에 기대고 있어서, 전면 `retry:false` 로 두면
 * 30분 토큰 만료 뒤 열려 있던 시료 화면이 "상태를 확인하지 못했습니다" 로
 * 굳는다. 반대로 429/5xx 재시도는 하루 300건 한도를 갉아먹으므로 하지 않는다.
 */
export function useEvidenceFiles(sampleId: number, enabled = true) {
  return useQuery<EvidenceFilesResponse>({
    queryKey: EVIDENCE_FILES_KEY(sampleId),
    queryFn: async () => (await samplesApi.evidenceFiles(sampleId)).data,
    enabled: enabled && Number.isFinite(sampleId) && sampleId > 0,
    retry: (failureCount, err) =>
      isAxiosError(err) && err.response?.status === 401 && failureCount < 2,
  });
}

function header(headers: unknown, name: string): string | null {
  // axios 는 응답 헤더 이름을 소문자로 준다.
  const h = headers as Record<string, unknown> | undefined;
  const value = h?.[name];
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** 받은 바이트를 실제로 해싱한다. 못 하면 못 했다고 말하기 위해 null 을 준다. */
async function sha256OfBlob(blob: Blob): Promise<string | null> {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return null;
    const digest = await subtle.digest("SHA-256", await blob.arrayBuffer());
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

/** 이름 기록이 없을 때 쓸 이름. 없는 확장자를 지어내지 않는다. */
function fallbackFileName(sampleId: number, kind: EvidenceFileKind): string {
  // `.txt` 를 붙이면 Agilent `.D`·mzML·vendor 바이너리를 텍스트라고 잘못
  // 기술하게 된다 — 증거물의 성격을 화면이 바꿔 부르는 셈이다.
  return `sample-${sampleId}-${kind}`;
}

export interface EvidenceDownloadState {
  /** 진행 중인 종류들. 하나만 담으면 다른 버튼이 되살아나 중복 열람이 된다. */
  pending: Partial<Record<EvidenceFileKind, true>>;
  /** 종류별 마지막 성공 증적. 한 칸만 두면 늦게 온 응답이 앞 경고를 지운다. */
  receipts: Partial<Record<EvidenceFileKind, EvidenceDownloadReceipt>>;
  /** 종류별 마지막 실패. */
  failures: Partial<Record<EvidenceFileKind, EvidenceFailure>>;
}

const EMPTY: EvidenceDownloadState = {
  pending: {},
  receipts: {},
  failures: {},
};

/**
 * 증거 원본을 내려받는다.
 *
 * **재시도하지 않는다.** 이 요청은 감사 VIEW 행을 남기므로, 자동 재시도는
 * 사람이 두 번 열람한 것처럼 보이는 기록을 만들고 하루 300건 한도를 갉아먹는다.
 * 같은 이유로 **진행 중인 종류의 재클릭도 무시**한다.
 */
export function useDownloadEvidenceFile(sampleId: number) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<EvidenceDownloadState>(EMPTY);

  // 시료가 바뀌면 직전 시료의 결과를 버린다. 라우터가 /samples/1 → /samples/2
  // 이동에서 페이지를 **언마운트하지 않기** 때문에, 이게 없으면 시료 A 에서
  // 받은 "무결성 확인됨" 증적이 시료 B 화면에 그대로 남는다 — 다른 시료의
  // 증거를 이 시료의 것처럼 보여 주는 것이라 절대 안 된다.
  // (React 공식 "prop 이 바뀔 때 state 조정" 패턴 — 낡은 값이 한 프레임
  //  보였다 사라지는 일이 없고, 의존성 배열 린트와도 싸우지 않는다.)
  const [lastSampleId, setLastSampleId] = useState(sampleId);
  if (sampleId !== lastSampleId) {
    setLastSampleId(sampleId);
    setState(EMPTY);
  }

  const download = useCallback(
    async (kind: EvidenceFileKind, recordedName: string | null) => {
      let alreadyRunning = false;
      setState((prev) => {
        if (prev.pending[kind]) {
          alreadyRunning = true;
          return prev;
        }
        const receipts = { ...prev.receipts };
        const failures = { ...prev.failures };
        delete receipts[kind];
        delete failures[kind];
        return {
          pending: { ...prev.pending, [kind]: true },
          receipts,
          failures,
        };
      });
      if (alreadyRunning) return;

      const clearPending = (s: EvidenceDownloadState) => {
        const pending = { ...s.pending };
        delete pending[kind];
        return pending;
      };

      try {
        const res = await samplesApi.downloadSpectrumFile(sampleId, kind);
        const blob = res.data as Blob;

        // 파일명은 목록이 준 **원래 이름**을 쓴다. 디스크 이름은 uuid 라
        // 사용자에게 의미가 없다.
        const name = recordedName || fallbackFileName(sampleId, kind);

        const serverSha = header(res.headers, "x-evidence-sha256");
        // 서버 헤더는 **스트리밍 전에 디스크에서** 계산된 값이라, 전송 중
        // 잘려도 그대로 온다. 받은 바이트를 직접 해싱해야 "내가 받은 것이
        // 저장소의 원본과 같은가" 를 실제로 말할 수 있다.
        const receivedSha = await sha256OfBlob(blob);

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        // 동기 revoke 는 브라우저에 따라 다운로드를 취소시킨다 — 다음 태스크로.
        setTimeout(() => {
          link.remove();
          URL.revokeObjectURL(url);
        }, 0);

        setState((prev) => ({
          pending: clearPending(prev),
          failures: prev.failures,
          receipts: {
            ...prev.receipts,
            [kind]: {
              kind,
              fileName: name,
              sizeBytes: blob.size,
              serverSha256: serverSha,
              receivedSha256: receivedSha,
              transferOk:
                serverSha && receivedSha ? serverSha === receivedSha : null,
              storedSha256: header(res.headers, "x-evidence-stored-hash"),
              storedHashScope: header(
                res.headers,
                "x-evidence-stored-hash-scope",
              ) as StoredHashScope | null,
              integrity: header(
                res.headers,
                "x-evidence-integrity",
              ) as EvidenceIntegrity | null,
            },
          },
        }));
      } catch (err) {
        const failure = await parseEvidenceFailure(err);
        setState((prev) => ({
          pending: clearPending(prev),
          receipts: prev.receipts,
          failures: { ...prev.failures, [kind]: failure },
        }));
        // 서버가 방금 "없다/소실" 이라고 답했는데 목록이 계속 "보관 중" 을
        // 보여 주면, 화면이 서버가 부정한 사실을 주장하게 된다.
        if (
          failure.code === "EVIDENCE_LOST" ||
          failure.code === "EVIDENCE_NEVER_STORED"
        ) {
          queryClient.invalidateQueries({
            queryKey: EVIDENCE_FILES_KEY(sampleId),
          });
        }
      }
    },
    [sampleId, queryClient],
  );

  return { ...state, download };
}
